import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import {
  Coins, LogOut, User, ShieldCheck,
  Sparkles, ShoppingBag, Compass, Library, Ticket, ArrowLeftRight,
  Swords, Flag, Wand2, ChevronsUp, Lightbulb, Drama, Trophy, Mountain, ScrollText, ShieldHalf,
  Boxes, Crown, Users, Map, Gift,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalChat } from "@/components/GlobalChat";
import { PresenceProvider } from "@/context/PresenceContext";
import { PagePresence } from "@/components/PagePresence";
import { AgeGate } from "@/components/AgeGate";
import { HowToGate } from "@/components/HowToGate";
import { TosGate } from "@/components/TosGate";
import AwardBroadcast from "@/components/AwardBroadcast";
import { PlayerHub } from "@/components/PlayerHub";
import { MentionOverlay } from "@/components/MentionOverlay";
import { ReleaseBadge } from "@/components/ReleaseBadge";
import { RpgMusicPlayer } from "@/components/RpgMusicPlayer";

/* Each tab gets its own neon accent so the bar reads like a control deck. */
const NAV_TABS = [
  { to: "/home", label: "Home", icon: Sparkles, color: "#22d3ee", testid: "nav-home", scope: "public" },
  { to: "/store", label: "Store", icon: ShoppingBag, color: "#34d399", testid: "nav-store", scope: "public" },
  { to: "/explore", label: "Explore", icon: Compass, color: "#38bdf8", testid: "nav-explore", scope: "public" },
  { to: "/collection", label: "Playden", icon: Library, color: "#a78bfa", testid: "nav-collection", scope: "auth" },
  { to: "/redeem", label: "Redeem", icon: Ticket, color: "#fbbf24", testid: "nav-redeem", scope: "auth" },
  { to: "/requests", label: "Trade", icon: ArrowLeftRight, color: "#f472b6", testid: "nav-trades", scope: "auth" },
  { to: "/arena", label: "Arena", icon: Swords, color: "#fb7185", testid: "nav-arena", scope: "auth", badge: "challenges" },
  { to: "/race", label: "Race", icon: Flag, color: "#4ade80", testid: "nav-race", scope: "auth" },
  { to: "/cavern", label: "Cavern", icon: Mountain, color: "#e879f9", testid: "nav-cavern", scope: "auth" },
  { to: "/adventure", label: "Adventure", icon: Compass, color: "#fbbf24", testid: "nav-adventure", scope: "auth" },
  { to: "/houses", label: "Houses", icon: Crown, color: "#f59e0b", testid: "nav-houses", scope: "public" },
  { to: "/guilds", label: "Guilds", icon: Users, color: "#38bdf8", testid: "nav-guilds", scope: "public" },
  { to: "/roadmap", label: "Roadmap", icon: Map, color: "#22d3ee", testid: "nav-roadmap", scope: "public" },
  { to: "/deep-dive", label: "Deep Dive", icon: Compass, color: "#a78bfa", testid: "nav-deep-dive", scope: "auth" },
  { to: "/path", label: "Path", icon: Trophy, color: "#fde047", testid: "nav-path", scope: "auth", badge: "quests" },
  { to: "/talentshow", label: "Talent Show", icon: Drama, color: "#fb923c", testid: "nav-talentshow", scope: "public" },
  { to: "/upgrade", label: "Upgrade", icon: Wand2, color: "#c084fc", testid: "nav-upgrade", scope: "auth" },
  { to: "/ascend", label: "Ascend", icon: ChevronsUp, color: "#f0abfc", testid: "nav-ascend", scope: "auth" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, color: "#2dd4bf", testid: "nav-admin", scope: "admin" },
];

function NavItem({ to, icon: Icon, color, children, testid, badge = 0, spanned = false }) {
  return (
    <NavLink
      to={to}
      end={to === "/home"}
      data-testid={testid}
      style={{ "--accent": color }}
      className={({ isActive }) =>
        `nav-tab group relative flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-[13px] font-medium whitespace-nowrap transition-all duration-300 min-h-[44px] sm:min-h-0 ${
          spanned ? "flex-1 min-w-0" : "w-full min-w-0"
        } ${isActive ? "text-white -translate-y-px" : "text-white/55 hover:text-white hover:-translate-y-px"}`
      }
    >
      {({ isActive }) => (
        <>
          {/* accent fill + ring (always on when active, on hover otherwise) */}
          <span
            className={`pointer-events-none absolute inset-0 rounded-xl border transition-all duration-300 ${
              isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            style={{
              background: `${color}1f`,
              borderColor: `${color}59`,
              boxShadow: isActive ? `0 0 22px ${color}4d, inset 0 0 12px ${color}1a` : "none",
            }}
          />
          <Icon
            className="relative h-[18px] w-[18px] shrink-0 transition-transform duration-300 group-hover:scale-110"
            style={{ color: isActive ? color : undefined }}
          />
          <span className="relative truncate tracking-wide">{children}</span>
          {badge > 0 && (
            <span
              data-testid={`${testid}-badge`}
              className="relative ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black font-mono text-[10px] font-bold animate-pulse"
            >
              {badge}
            </span>
          )}
          {/* underline beam */}
          <span
            className="pointer-events-none absolute -bottom-[3px] left-1/2 -translate-x-1/2 h-[2px] rounded-full transition-all duration-300"
            style={{
              width: isActive ? "62%" : "0%",
              background: color,
              boxShadow: `0 0 10px ${color}, 0 0 4px ${color}`,
            }}
          />
        </>
      )}
    </NavLink>
  );
}

export function Layout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(user?.credits ?? 0);
  const [giftCredits, setGiftCredits] = useState(user?.gift_credits ?? 0);
  const [sp, setSp] = useState(user?.sp ?? 0);
  const [pendingChallenges, setPendingChallenges] = useState(0);
  const [claimableQuests, setClaimableQuests] = useState(0);

  useEffect(() => { setCredits(user?.credits ?? 0); setGiftCredits(user?.gift_credits ?? 0); setSp(user?.sp ?? 0); }, [user?.credits, user?.gift_credits, user?.sp]);

  // Credits/SP come straight from AuthContext.user, which now refreshes
  // instantly after any action (api interceptor), on tab focus, and on a slow
  // safety poll — so the header no longer needs its own /auth/me poll.

  // Poll incoming arena challenges so collectors always see when they're called out
  useEffect(() => {
    if (!user?.id) return;
    const poll = async () => {
      try {
        const r = await api.get("/arena/battles");
        setPendingChallenges((r.data?.incoming || []).length);
      } catch (_) { /* silent */ }
    };
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Poll progression so the Path tab badges claimable quests
  useEffect(() => {
    if (!user?.id) return;
    const poll = async () => {
      try {
        const r = await api.get("/progression/summary");
        setClaimableQuests(r.data?.claimable_count || 0);
      } catch (_) { /* silent */ }
    };
    poll();
    const interval = setInterval(poll, 12000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Header behavior — full-screen game feel:
  //   • Hide on scroll-down past a small threshold.
  //   • DO NOT reveal on ordinary scroll-up (users flicking around the
  //     battle log / action list shouldn't summon the nav).
  //   • Reveal ONLY when the user deliberately swipes DOWN from very
  //     near the top edge of the viewport (touchstart within the top
  //     32 px + a subsequent 40+ px downward drag). Same gesture on a
  //     mouse (mousedown near the top + drag down) also works so power
  //     users on a laptop trackpad get the same escape hatch.
  //   • Also reveal when scrollY returns to the very top (scrollY < 8).
  const [hideHeader, setHideHeader] = useState(false);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const last = lastScrollY.current;
      if (y < 8) setHideHeader(false);
      else if (y > last + 6) setHideHeader(true);
      // NB: no "else if (y < last - 6) setHideHeader(false)" any more —
      // scroll-up alone must not summon the header.
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    // Top-edge pull-down gesture — the ONLY way to bring the nav back
    // once it's hidden mid-battle. Threshold: touch/pointer must start
    // within the top 32 px of the viewport, drag must be a 40+ px net
    // downward motion.
    let startY = null;
    let startX = null;
    const EDGE_PX = 32;
    const REVEAL_DELTA = 40;
    const onStart = (clientX, clientY) => {
      if (clientY <= EDGE_PX) { startY = clientY; startX = clientX; }
    };
    const onMove = (clientX, clientY) => {
      if (startY == null) return;
      const dy = clientY - startY;
      const dx = Math.abs(clientX - startX);
      if (dy >= REVEAL_DELTA && dx < 60) { setHideHeader(false); startY = null; }
    };
    const onEnd = () => { startY = null; startX = null; };
    const ts = (e) => { const t = e.touches[0]; onStart(t.clientX, t.clientY); };
    const tm = (e) => { const t = e.touches[0]; onMove(t.clientX, t.clientY); };
    // Mouse mirror for trackpad users who prefer clicking + dragging.
    const md = (e) => onStart(e.clientX, e.clientY);
    const mm = (e) => onMove(e.clientX, e.clientY);
    window.addEventListener("touchstart", ts, { passive: true });
    window.addEventListener("touchmove", tm, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    window.addEventListener("mousedown", md);
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", onEnd);
    return () => {
      window.removeEventListener("touchstart", ts);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
      window.removeEventListener("mousedown", md);
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", onEnd);
    };
  }, []);

  const isAdmin = user?.role === "admin";
  const visibleTabs = NAV_TABS.filter((t) =>
    t.scope === "public" || (t.scope === "auth" && user) || (t.scope === "admin" && isAdmin)
  );
  const twoRows = visibleTabs.length > 6;
  const half = Math.ceil(visibleTabs.length / 2);
  const rows = twoRows ? [visibleTabs.slice(0, half), visibleTabs.slice(half)] : [visibleTabs];

  const badgeFor = (t) => (t.badge === "challenges" ? pendingChallenges : t.badge === "quests" ? claimableQuests : 0);

  return (
    <PresenceProvider>
    <div className="min-h-screen flex flex-col">
      <AwardBroadcast />
      <MentionOverlay />
      <header className={`sticky top-0 z-30 backdrop-blur-xl bg-[hsl(var(--background))]/80 border-b border-white/5 transition-transform duration-300 ease-out ${hideHeader ? "-translate-y-full pointer-events-none" : "translate-y-0"}`} data-testid="app-header" aria-hidden={hideHeader ? "true" : "false"}>
        {/* Top row — brand + account deck */}
        <div data-testid="header-account-row" className="max-w-7xl mx-auto px-3 sm:px-6 min-h-[56px] sm:min-h-16 py-1.5 sm:py-2 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          {user ? (
            <PlayerHub>
              <Link to="/home" data-testid="brand-link" className="flex items-center gap-2 sm:gap-3 group shrink-0 min-w-0" data-hub-trigger="true">
                <div className="relative h-10 w-10 sm:h-[110px] sm:w-[110px] rounded-xl sm:rounded-2xl overflow-hidden bg-black shadow-[0_0_18px_rgba(0,220,255,0.35)] sm:shadow-[0_0_28px_rgba(0,220,255,0.45)] border border-white/15 transition-shadow group-hover:shadow-[0_0_38px_rgba(0,220,255,0.7)] shrink-0">
                  <img src="/brand/playden-black.png" alt="Playden" className="absolute inset-0 w-full h-full object-contain p-0.5" />
                </div>
                <span className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
                  <span className="font-display text-base sm:text-2xl tracking-[0.14em] sm:tracking-[0.22em] text-white group-hover:text-cyan-300 transition-colors">PLAYDEN</span>
                  <ReleaseBadge testId="app-version-badge" />
                </span>
              </Link>
            </PlayerHub>
          ) : (
            <Link to="/home" data-testid="brand-link" className="flex items-center gap-2 sm:gap-3 group shrink-0 min-w-0">
              <div className="relative h-10 w-10 sm:h-[110px] sm:w-[110px] rounded-xl sm:rounded-2xl overflow-hidden bg-black shadow-[0_0_18px_rgba(0,220,255,0.35)] sm:shadow-[0_0_28px_rgba(0,220,255,0.45)] border border-white/15 transition-shadow group-hover:shadow-[0_0_38px_rgba(0,220,255,0.7)] shrink-0">
                <img src="/brand/playden-black.png" alt="Playden" className="absolute inset-0 w-full h-full object-contain p-0.5" />
              </div>
              <span className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
                <span className="font-display text-base sm:text-2xl tracking-[0.14em] sm:tracking-[0.22em] text-white group-hover:text-cyan-300 transition-colors">PLAYDEN</span>
                <ReleaseBadge testId="app-version-badge-public" />
              </span>
            </Link>
          )}

          <div data-testid="header-account-controls" className="flex flex-wrap items-center justify-end gap-1 sm:gap-2 min-w-0 max-w-full ml-auto">
            <RpgMusicPlayer />
            <Link to="/" data-testid="portal-link" title="Back to Playden Studios portal" className="hidden sm:inline-flex hud-pill px-3 py-1.5 rounded-full items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.28em] text-white/55 hover:text-cyan-300 transition-colors">
              <Boxes className="h-3.5 w-3.5" />
              <span>Studios</span>
            </Link>
            {user ? (
              <>
                <div className="hud-pill px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1.5 sm:gap-2" data-testid="credits-balance-display" title={giftCredits > 0 ? `Includes ${giftCredits.toLocaleString()} Gift Credits (spendable on yourself, never transferable)` : "Credits"}>
                  <Coins className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-400" />
                  <span className="font-mono text-[12px] sm:text-sm text-white tabular-nums">{credits.toLocaleString()}</span>
                  {giftCredits > 0 && (
                    <span className="flex items-center gap-0.5 pl-1 ml-0.5 border-l border-white/15 text-emerald-300" data-testid="gift-credits-display" title="Gift Credits — spend on yourself, never transferable">
                      <Gift className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      <span className="font-mono text-[11px] sm:text-xs tabular-nums">{giftCredits.toLocaleString()}</span>
                    </span>
                  )}
                </div>
                <div className="hud-pill px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-1.5 sm:gap-2" data-testid="sp-balance-display" title="Skill Points">
                  <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-fuchsia-400" />
                  <span className="font-mono text-[12px] sm:text-sm text-white tabular-nums">{sp.toLocaleString()}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 hidden sm:block">SP</span>
                </div>
                <NotificationBell />
                <Link to="/profile" data-testid="profile-link" className="hud-pill px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex items-center gap-2 min-w-0 max-w-full text-sm text-white hover:bg-white/5 transition-colors">
                  <User className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:block break-all">{user.display_name}</span>
                </Link>
                <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} data-testid="logout-button" aria-label="Logout" className="h-9 w-9 sm:h-10 sm:w-10">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" size="sm" className="px-2 sm:px-3">Sign in</Button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 px-3 sm:px-4">Join</Button>
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="neon-divider" />

        {/* Tab deck — two spanned rows on desktop, single-row scroll on mobile */}
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-1.5 sm:py-2.5">
          {/* Desktop / tablet: spanned rows */}
          <nav className="hidden md:flex md:flex-col gap-2" data-testid="primary-nav">
            {rows.map((row, ri) => (
              <div key={row.map((tab) => tab.to).join("|")} className="flex items-stretch gap-2">
                {row.map((t) => (
                  <NavItem key={t.to} to={t.to} icon={t.icon} color={t.color} testid={t.testid} badge={badgeFor(t)} spanned>
                    {t.label}
                  </NavItem>
                ))}
              </div>
            ))}
          </nav>

          {/* Mobile: single horizontally-scrollable row. `overscroll-x-contain`
              prevents iOS back-swipes when panning the nav; `touch-pan-x`
              makes horizontal swiping the only recognized gesture inside the
              strip so vertical scroll on the page still works. */}
          <nav aria-label="Game navigation" className="md:hidden grid grid-cols-2 auto-rows-[44px] gap-1.5 max-h-24 overflow-y-auto overscroll-y-contain touch-pan-y pb-1 min-w-0 [scrollbar-width:thin]" data-testid="primary-nav-mobile">
            {visibleTabs.map((t) => (
              <NavItem key={t.to} to={t.to} icon={t.icon} color={t.color} testid={`${t.testid}-mobile`} badge={badgeFor(t)}>
                {t.label}
              </NavItem>
            ))}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <GlobalChat />
      <PagePresence />
      <AgeGate />
      <HowToGate />
      <TosGate />

      <footer className="mt-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="font-mono text-xs text-white/40 uppercase tracking-[0.3em]">PLAYDEN · Digital Card Collection</div>
          <div data-testid="footer-primary-links" className="flex flex-wrap items-center justify-center gap-4 min-w-0 max-w-full">
            <Link to="/terms" data-testid="footer-terms-link" className="font-mono text-xs text-white/50 hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5">
              <ScrollText className="h-3.5 w-3.5" /> Terms of Service
            </Link>
            <Link to="/legal/privacy" data-testid="footer-privacy-link" className="font-mono text-xs text-white/50 hover:text-emerald-300 transition-colors inline-flex items-center gap-1.5">
              <ShieldHalf className="h-3.5 w-3.5" /> Privacy Policy
            </Link>
            <Link to="/help" data-testid="footer-help-link" className="font-mono text-xs text-white/50 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" /> How to Play
            </Link>
            <span className="font-mono text-xs text-white/40">Built for collectors</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-white/[0.03] pt-4">
          <Link to="/legal/ugc-rules" data-testid="footer-ugc-link" className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-rose-300 transition-colors">UGC Rules</Link>
          <span className="text-white/15">·</span>
          <Link to="/legal/dmca" data-testid="footer-dmca-link" className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-amber-300 transition-colors">DMCA / Copyright</Link>
          <span className="text-white/15">·</span>
          <Link to="/legal/refunds" data-testid="footer-refunds-link" className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-cyan-300 transition-colors">Refund &amp; Dispute Policy</Link>
          <span className="text-white/15">·</span>
          <Link to="/changelog" data-testid="footer-changelog-link" className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-cyan-300 transition-colors">Patch Notes</Link>
          <span className="text-white/15">·</span>
          <Link to="/roadmap" data-testid="footer-roadmap-link" className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-cyan-300 transition-colors">Roadmap</Link>
        </div>
      </footer>
    </div>
    </PresenceProvider>
  );
}
